import './PartEditForm.scss';
import {ApItemVO} from 'api/ApItemVO';
import {ApCreateTypeVO} from 'api/ApCreateTypeVO';
import {RulDataTypeCodeEnum} from 'api/RulDataTypeCodeEnum';
import {RulDescItemTypeExtVO} from 'api/RulDescItemTypeExtVO';
import {RulDataTypeVO} from 'api/RulDataTypeVO';
import {RequiredType} from 'api/RequiredType';
import * as ItemInfo from 'utils/ItemInfo';
import { compareItems, findItemPlacePosition } from 'utils/partEdit';
import {ApItemBitVO} from 'api/ApItemBitVO';
import {WebApi} from 'actions/WebApi';
import { RefTablesState } from 'typings/store'
import { ApViewSettingRule, ApViewSettings } from 'api/ApViewSettings';
import {ApAccessPointCreateVO} from 'api/ApAccessPointCreateVO';
// import { ApPartFormVO } from "api/ApPartFormVO";
import { compareCreateTypes, hasItemValue } from 'utils/ItemInfo';
import { DetailStoreState } from 'types';
import { RevisionItem } from '../../revision';
import { RevisionApPartForm } from '../form';
import { AutoValue } from 'elza-api';
import { ApItemStringVO } from 'api/ApItemStringVO';

export const addEmptyItems = (
    attributes: Array<ApCreateTypeVO>,
    refTables: RefTablesState,
    formItems: RevisionItem[],
    partTypeId: number,
    arrayInsert: (index: number, value: RevisionItem) => void,
    userAction: boolean,
    apViewSettings?: ApViewSettingRule,
) => {
    let emptyItems = attributes.map((attribute) => createEmptyItem(attribute, refTables, userAction));

    // Vložení do formuláře - od konce
    emptyItems.reverse().forEach(item => {
        let index = findItemPlacePosition(item, formItems, partTypeId, refTables, apViewSettings);
        arrayInsert(index, item);
    });
}

export const createAutoValueItemWithIndex = (
    attribute: ApCreateTypeVO,
    value: AutoValue,
    refTables: RefTablesState,
    formItems: RevisionItem[],
    partTypeId: number,
    apViewSettings?: ApViewSettingRule,
) => {
    const item = createAutoValueItems(attribute, value, refTables);

    const index = findItemPlacePosition(item, formItems, partTypeId, refTables, apViewSettings);
    return {item, index};
}

// creates item from auto value
const createAutoValueItems = (
    attribute: ApCreateTypeVO,
    value: AutoValue,
    refTables: RefTablesState,
):RevisionItem<ApItemStringVO> => {
    const itemType = refTables.descItemTypes.itemsMap[attribute.itemTypeId] as RulDescItemTypeExtVO;
    const dataType = refTables.rulDataTypes.itemsMap[itemType.dataTypeId] as RulDataTypeVO;

    switch(dataType.code){
        case RulDataTypeCodeEnum.STRING:{
            const item: ApItemStringVO = {
                typeId: attribute.itemTypeId,
                '@class': ItemInfo.getItemClass(dataType.code),
                position: 1, // TODO: dořešit pozici
                value: value?.value,
                specId: value?.itemSpecId,
            }
            return {
                updatedItem: item,
                typeId: attribute.itemTypeId,
                '@class': ItemInfo.getItemClass(dataType.code),
            };
        }
        default:
            return undefined;
    }
}

let tempIdCounter = 0;
// Extending APItemVO with temporary id for newly created desc items, that don't have id from db.
// Used for 'key' attribute.
export type ApItemVOWithTempId = ApItemVO & {tempId: number};

const createEmptyItem = (attribute: ApCreateTypeVO, refTables: RefTablesState, userAction: boolean) => {
    const itemType = refTables.descItemTypes.itemsMap[attribute.itemTypeId] as RulDescItemTypeExtVO;
    const dataType = refTables.rulDataTypes.itemsMap[itemType.dataTypeId] as RulDataTypeVO;

    const item: ApItemVOWithTempId = {
        typeId: attribute.itemTypeId,
        '@class': ItemInfo.getItemClass(dataType.code),
        position: 1, // TODO: dořešit pozici
        tempId: tempIdCounter++,
    };


    // Implicitní hodnoty
    switch (dataType.code) {
        case RulDataTypeCodeEnum.BIT:
            ((item as unknown) as ApItemBitVO).value = false;
            break;
    }

    // Implicitní specifikace - pokud má specifikaci a má právě jednu položku a současně jde o povinnou hodnotu
    // Pokud uživatel přidal ručně i pro nepovinné
    if (itemType.useSpecification && (attribute.requiredType === RequiredType.REQUIRED || userAction)) {
        if (attribute.itemSpecIds && attribute.itemSpecIds.length === 1) {
            item.specId = attribute.itemSpecIds[0];
        }
    }

    return {
        updatedItem: item,
        typeId: attribute.itemTypeId,
        '@class': ItemInfo.getItemClass(dataType.code),
    };
}

export const getUpdatedForm = async (
    data: RevisionApPartForm,
    typeId: number,
    scopeId: number,
    apViewSettings: DetailStoreState<ApViewSettings>,
    refTables: RefTablesState,
    partTypeId: number,
    partId?: number,
    parentPartId?: number,
    accessPointId?: number,
    revParentPartId?: number,
) => {
    const apViewSettingRule = apViewSettings.data!.rules[apViewSettings.data!.scopeRuleSetMap[scopeId]];
    const items = data.items.filter((item) =>
        item.updatedItem?.changeType !== "DELETED"
    ).map((item) =>
        item.updatedItem || item.item as any
    );
    // console.log("get updated form items", items, data, parentPartId, revParentPartId);
    const form: ApAccessPointCreateVO = {
        typeId,
        partForm: {
            ...data,
            parentPartId,
            revParentPartId,
            items: [...items.filter(hasItemValue)],
            partId: partId,
        },
        accessPointId,
        scopeId,
    };

    const { attributes, errors } = await WebApi.getAvailableItems(form);

    attributes.sort((a, b) => {
        return compareCreateTypes(a, b, partTypeId, refTables, apViewSettingRule);
    });

    return {
        attributes,
        errors,
        data: {
            ...data,
            items: getItemsWithRequired(data.items, attributes, partTypeId, refTables, apViewSettingRule),
        } as RevisionApPartForm
    }
};

export const getItemsWithRequired = (
    items: RevisionItem[],
    attributes: ApCreateTypeVO[],
    partTypeId: number,
    refTables: RefTablesState,
    apViewSettings?: ApViewSettingRule,
) => {
    const newItems: RevisionItem[] = [];
    addEmptyItems(
        getRequiredAttributes(items, attributes),
        refTables,
        items,
        partTypeId,
        (_index, item) => {newItems.push(item)},
        false,
        apViewSettings,
    )
    return [...items, ...newItems].sort((a, b) => compareItems(a, b, partTypeId, refTables, apViewSettings));
}

const getRequiredAttributes = (items: RevisionItem[], attributes: ApCreateTypeVO[]) => {
    const existingItemTypeIds = items.map(i => i.typeId);
    const requiredAttributes = attributes.filter(attributes => {
        if (attributes.requiredType === RequiredType.REQUIRED) {
            return existingItemTypeIds.indexOf(attributes.itemTypeId) < 0;
        } else {
            return false;
        }
    });
    return requiredAttributes;
}
